import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Simple ErrorBoundary for the multi-card dashboard.
 * Prevents a single card/render error from white-screening the entire SPA.
 * Logs to console (and could be extended to cloudStore.writeLog).
 * Per 2026-05-26 multi-agent code review recommendation.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error in card/tree:', error, errorInfo);
    // Future: if auth available, writeLog('error', 'ui', error.message, { stack: error.stack, componentStack: errorInfo.componentStack })
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div style={{ padding: 16, border: '1px solid #f00', background: '#fff0f0', color: '#900', borderRadius: 4, margin: 8 }}>
          <strong>Något gick fel i det här kortet / vyn.</strong>
          <div style={{ fontSize: '0.85em', marginTop: 4 }}>
            {this.state.error?.message || 'Okänt fel'} — prova att refresha eller byta bolag/år.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;